import { useState } from 'react';
import { commentThread } from '@/mocks/comments';
import Reveal from '@/components/base/Reveal';

export default function CommentSection() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState('');
  const [sent, setSent] = useState(false);

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSend = () => {
    if (!draft.trim()) return;
    setSent(true);
    setDraft('');
  };

  return (
    <section id="comments" className="w-full bg-background-50 px-4 py-16 md:px-8 md:py-20">
      <div className="mx-auto w-full max-w-7xl">
        <Reveal className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-[11px] tracking-[0.24em] text-primary-600">
              ON SCENE / 围观现场
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              半夜还在讨论的人，都在这儿
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            评论区按时间倒序，先把观点摆出来，再决定要不要跟。这里的每条讨论都会回到对应条线。
          </p>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50 dark:text-foreground-950">
                  我
                </span>
                <input
                  type="text"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="说说你的判断，或者补充一条你没看到的节点…"
                  className="h-11 w-full flex-1 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-300"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  <i className="ri-send-plane-fill text-base" />
                  发布
                </button>
              </div>
              {sent && (
                <p className="mt-3 flex items-center gap-2 text-xs text-secondary-700">
                  <i className="ri-checkbox-circle-line text-base" />
                  示例站点：评论仅作展示，不会真正提交。
                </p>
              )}
            </div>

            <div className="mt-4 space-y-3">
              {commentThread.comments.map((comment, index) => {
                const isLiked = !!liked[comment.id];
                return (
                  <Reveal key={comment.id} delay={index * 45}>
                    <article className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                          {comment.initial}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-sm font-medium text-foreground-900">
                              {comment.author}
                            </span>
                            <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                              {comment.role}
                            </span>
                            <span className="font-label text-[11px] text-foreground-400">
                              {comment.time}
                            </span>
                          </div>
                          <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                            {comment.content}
                          </p>
                          <div className="mt-3 flex items-center gap-4">
                            <button
                              type="button"
                              onClick={() => toggleLike(comment.id)}
                              className={`flex items-center gap-1.5 text-xs transition-colors ${
                                isLiked ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                              }`}
                            >
                              <i
                                className={
                                  isLiked ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'
                                }
                              />
                              {comment.likes + (isLiked ? 1 : 0)}
                            </button>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-secondary-700"
                            >
                              <i className="ri-chat-3-line text-base" />
                              {comment.replies}
                            </button>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-800"
                            >
                              <i className="ri-share-forward-line text-base" />
                              转发
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="rounded-lg bg-foreground-950 p-5 text-background-50 md:p-6">
                <h3 className="font-heading text-lg font-bold text-background-50">现场实时数据</h3>
                <p className="mt-1 text-xs text-background-50/60">每 15 分钟刷新一次</p>
                <div className="mt-5 space-y-4">
                  {commentThread.stats.map((stat) => (
                    <div
                      key={stat.id}
                      className="flex items-center justify-between border-b border-background-50/10 pb-4 last:border-b-0 last:pb-0"
                    >
                      <span className="text-sm text-background-50/70">{stat.label}</span>
                      <span className="font-heading text-xl font-bold text-accent-300">
                        {stat.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                <h3 className="font-heading text-base font-bold text-accent-900">今日热议条线</h3>
                <ul className="mt-3 space-y-3">
                  {['男团解约风波', '综艺剪辑争议', '代言联动删除'].map((topic, index) => (
                    <li key={topic} className="flex items-center gap-3">
                      <span className="font-label text-sm font-bold text-accent-500">
                        0{index + 1}
                      </span>
                      <span className="text-sm text-foreground-700">{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}